import React, { useState, useCallback, useEffect } from 'react';
import { Field, reduxForm, formValueSelector, FieldArray } from 'redux-form';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useSnackbar } from 'notistack';
import moment from 'moment';


import { CATEGORIES } from 'pages/create-activity/constants';


import {
  
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  IconButton,
  Collapse,
  Box,
  FormHelperText,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import {
  FormActionButtons,
  CustomDateRangePicker,
  Input,
  AppHeader,
  CategoriesPrices,
  CircularLoader
} from 'components';


import {
  CREATE_ACTIVITY_RATE_PLAN,
  DELETE_ACTIVITY_RATE_PLAN
} from 'graphql/mutations/activity';
import { GET_ACTIVITY_RATE_PLANS } from 'graphql/queries/activities';


import useStyles from 'pages/rate-plan/styles';

const FORM_NAME = 'ActivityRatePlanForm';

const initialValues = {
  title: '',
  categories: [
    {
      participant: 'Adult',
      USD: '0',
      EUR: '0',
      NOK: '0'
    }
  ],
  dateRange: null
};


const validateDateRange = dateRange => {
  if (!dateRange) return false;
  return (
    moment(dateRange.startDate).isValid() && moment(dateRange.endDate).isValid()
  );
};

const validateCategories = categories => {
  if (!categories || categories.length === 0) return false;
  return categories.every(category => {
    const hasParticipant = category.participant?.trim() !== '';
    const hasValue = [category.USD, category.EUR, category.NOK].some(
      v => parseFloat(v) > 0
    );
    return hasParticipant && hasValue;
  });
};

const formatDateRange = (startDate, endDate) => {
  return `${moment(startDate).format('MMM D, YYYY')} - ${moment(endDate).format(
    'MMM D, YYYY'
  )}`;
};

const pivotCurrencyValues = dateRanges => {
  const grouped = {};

  dateRanges?.forEach(({ startDate, endDate, categories, currency, value }) => {
    const key = `${startDate}-${endDate}-${categories}`;

    if (!grouped[key]) {
      grouped[key] = {
        startDate,
        endDate,
        categories
      };
    }

    grouped[key][currency] = value;
  });

  return Object.values(grouped);
};


const PriceGroupRow = ({ 
  group, 
  onDelete, 
  isDeleting, 
  isCurrentDeleting,
  title 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const classes = useStyles();
  const [t] = useTranslation();
    
  
  const getDateRange = () => {
    if (group.startDate && group.endDate) {
      // For locally added groups
      return formatDateRange(group.startDate, group.endDate);
    } else if (group.dateRanges && group.dateRanges.length > 0) {
      // For groups from backend
      const firstRange = group.dateRanges[0];
      return formatDateRange(firstRange.startDate, firstRange.endDate);
    }
    return '';
  };


  const getTransformedData = () => {
    if (group.categories) {
  
      return group.categories.map(category => ({
        participant: category.participant,
        USD: category.USD,
        EUR: category.EUR,
        NOK: category.NOK,
      }));
    } else if (group.dateRanges) {

      const participantMap = {};
      
      group.dateRanges.forEach(item => {
        if (!participantMap[item.categories]) {
          participantMap[item.categories] = {
            participant: item.categories,
            USD: 0,
            EUR: 0,
            NOK: 0,
          };
        }
        participantMap[item.categories][item.currency] = item.value;
      });
      
      return Object.values(participantMap);
    }
    return [];
  };

  const transformedData = getTransformedData();

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton 
            size="small" 
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={t('Expand price group details')}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {group.title || t('Untitled Rate Plan')}
        </TableCell>
        <TableCell>
          {getDateRange()}
        </TableCell>
        <TableCell align="center" style={{ verticalAlign: 'middle' }}>
          <IconButton 
            onClick={() => onDelete(group.id)}
            size="small"
            disabled={isDeleting}
            aria-label={t('Delete price group')}
          >
            {isCurrentDeleting ? (
              <CircularProgress size={24} />
            ) : (
              <HighlightOffIcon />
            )}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Table size="small" aria-label="price group details">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('Category')}</TableCell>
                    <TableCell align="right">NOK</TableCell>
                    <TableCell align="right">EUR</TableCell>
                    <TableCell align="right">USD</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transformedData.map((category, index) => (
                    <TableRow key={`${group.id}-category-${index}`}>
                      <TableCell component="th" scope="row">
                        {category.participant}
                      </TableCell>
                      <TableCell align="right">
                        {category.NOK}
                      </TableCell>
                      <TableCell align="right">
                        {category.EUR}
                      </TableCell>
                      <TableCell align="right">
                        {category.USD}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
const ActivityRatePlan = ({
  handleSubmit,
  setSubmitButton,
  isUpdate,
  dateRange,
  change,
  formValues,
  businessOwner,
  initialValues: initialValues,
  getFormValues
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [priceGroups, setPriceGroups] = useState([]);
  const [validationError, setValidationError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

   React.useLayoutEffect(() => {
       const updateSize = () => {
         setWindowWidth(window.innerWidth);
       };
       window.addEventListener('resize', updateSize);
       updateSize();
       return () => window.removeEventListener('resize', updateSize);
     }, []);

 
  const [createRatePlan, { loading: isCreating }] = useMutation(
    CREATE_ACTIVITY_RATE_PLAN
  );
  const [deleteRatePlan, { loading: isDeleting }] = useMutation(
    DELETE_ACTIVITY_RATE_PLAN
  );

  const { data, loading: isLoading, refetch } = useQuery(
    GET_ACTIVITY_RATE_PLANS,
    {
      variables: {
        businessOwnerId:
          businessOwner.selectedBusinessOwner.ownerId ||
          businessOwner.selectedBusinessOwner.id
      },
      fetchPolicy: 'no-cache'
    }
  );

  
  useEffect(() => {
    if (data?.activityRatePlans) {
      const formattedPlans = data.activityRatePlans.map(plan => {
        const dateGroups = plan.prices.reduce((acc, price) => {
          const dateKey = `${price.startDate}-${price.endDate}-${price.participant.participant}-${price.currency.currency}`;

          if (!acc[dateKey]) {
            acc[dateKey] = {
              startDate: price.startDate,
              endDate: price.endDate,
              categories: price.participant.participant,
              currency: price.currency.currency,
              value: price.value
            };
          }

          return acc;
        }, {});

        return {
          id: plan.id,
          title: plan.title || 'Untitled Rate Plan',
          dateRanges: Object.values(dateGroups)
        };
      });
      setPriceGroups(formattedPlans);
    }
  }, [data]);

  
  const handleAddPriceGroup = useCallback(
    async (categoriesFieldArray, getFormValues) => {
      // Prevent double-click issues
      if (isProcessing) {
        return;
      }

      setIsProcessing(true);
      setValidationError('');

     
      await new Promise(resolve => setTimeout(resolve, 50));

      try {
        
        const currentFormValues = getFormValues ? getFormValues() : formValues;
        const currentDateRange = currentFormValues?.dateRange || dateRange;
        const currentCategories = currentFormValues?.categories || [];
        const currentTitle = currentFormValues?.title || '';

       
        if (!validateDateRange(currentDateRange)) {
          setValidationError(t('Please select a valid date range'));
          return;
        }

       
        if (!validateCategories(currentCategories)) {
          setValidationError(
            t('Please add at least one category with valid prices')
          );
          return;
        }


        const tempId = `temp-${Date.now()}`;
        const newGroup = {
          id: tempId,
          title: currentTitle || t('Untitled Rate Plan'),
          startDate: moment(currentDateRange.startDate).format('YYYY-MM-DD'),
          endDate: moment(currentDateRange.endDate).format('YYYY-MM-DD'),
          categories: currentCategories.map(category => ({
            participant: category.participant,
            USD: parseFloat(category.USD) || 0,
            EUR: parseFloat(category.EUR) || 0,
            NOK: parseFloat(category.NOK) || 0
          }))
        };

      
        setPriceGroups(prev => [...prev, newGroup]);

       
        categoriesFieldArray.removeAll();
        categoriesFieldArray.push({
          participant: 'Adult',
          USD: '0',
          EUR: '0',
          NOK: '0'
        });

        change('dateRange', null);
        change('title', '');

        
        setValidationError('');
      } catch (error) {
        setValidationError(t('Failed to add price group. Please try again.'));
      } finally {
        setIsProcessing(false);
      }
    },
    [dateRange, formValues, change, t, isProcessing]
  );

  const handleDeleteGroup = async groupId => {
    if (!groupId) return;

    try {
      // If it's a temporary group (not saved to backend yet)
      if (groupId.toString().startsWith('temp-')) {
        setPriceGroups(prev => prev.filter(g => g.id !== groupId));
        setGroupToDelete(null);
        return;
      }

      // Delete from backend
      await deleteRatePlan({
        variables: {
          id: groupId,
          businessOwnerId: businessOwner.selectedBusinessOwner.id
        }
      });

      enqueueSnackbar(t('Rate plan deleted successfully'), {
        variant: 'success'
      });

      setPriceGroups(prev => prev.filter(g => g.id !== groupId));
    } catch (error) {
      enqueueSnackbar(t('Failed to delete rate plan'), { variant: 'error' });
    } finally {
      setGroupToDelete(null);
    }
  };

  const getAvailableCategories = () => {
    const usedCategories =
      formValues?.categories?.map(item => item.participant) || [];
    return CATEGORIES.filter(x => !usedCategories.includes(x));
  };

  const handleFormSubmit = async formValues => {
    if (priceGroups.length === 0) {
      setValidationError(t('Please add at least one price group'));
      return;
    }

    try {
      setIsProcessing(true);
      setValidationError('');

      const titleSet = new Set();

      const prices = priceGroups.flatMap(group => {
        // Skip groups that are already saved (have dateRanges)
        if (group.dateRanges) {
          return [];
        }

        titleSet.add(group.title || t('Untitled Rate Plan'));

        return group.categories.flatMap(category =>
          Object.entries({
            USD: category.USD,
            EUR: category.EUR,
            NOK: category.NOK
          })
            .filter(([_, value]) => parseFloat(value) > 0)
            .map(([currency, value]) => ({
              participant: category.participant,
              currency,
              value: parseFloat(value),
              startDate: group.startDate,
              endDate: group.endDate,
              title: group.title
            }))
        );
      });

      if (prices.length === 0) {
        setValidationError(t('No new price groups to save'));
        return;
      }

      const ratePlans = Array.from(titleSet).map(title => ({
        title: String(title),
        prices,
        businessOwnerId: businessOwner.selectedBusinessOwner.id
      }));

      const response = await createRatePlan({
        variables: {
          ratePlan: ratePlans
        }
      });

      if (response.data) {
        enqueueSnackbar(t('Rate plan created successfully'), {
          variant: 'success'
        });

        // Reset everything
        setPriceGroups([]);
        change('title', '');
        change('dateRange', null);
        change('categories', [
          {
            participant: 'Adult',
            USD: '0',
            EUR: '0',
            NOK: '0'
          }
        ]);

        await refetch();
      }
    } catch (error) {
      
      enqueueSnackbar(error.message || t('Failed to create rate plan'), {
        variant: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <CircularLoader
        isLoading={isLoading || isCreating || isDeleting}
        loaderClassName={classes.loaderClassName}
        className={classes.loaderWrapper}
      />
      <AppHeader />
      <div className={classes.root}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Field
            name="title"
            label=" Activity Rate Plan"
            component={Input}
            className={classes.inputClassName}
            placeholder={t('Add Your Rate Plan Title')}
          />

          <div className={classes.datePickerWrapper}>
            <Field
              name="dateRange"
              label="Select Dates For Rate Plan"
              component={CustomDateRangePicker}
              inputClassName={classes.inputClassName}
              numberOfMonths={
                windowWidth > 1010 ? 2 : 1 || windowWidth <= 750 ? 1 : 2
              }
              daySize={windowWidth > 600 ? 38 : 41}
              RatePlan
            />
          </div>

          {validationError && (
            <FormHelperText error className={classes.errorMessage}>
              {validationError}
            </FormHelperText>
          )}

          <div className={classes.currencyRow}>
            <div className={classes.fields}>
              <FieldArray
                name="categories"
                component={CategoriesPrices}
                availableCategories={getAvailableCategories()}
              />
            </div>
          </div>

          <div className={classes.buttonGroup}>
            <FieldArray
              name="categories"
              component={({ fields }) => (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async e => {
                    e.preventDefault();
                    e.stopPropagation();

                    
                    if (document.activeElement && document.activeElement.blur) {
                      document.activeElement.blur();
                    }

                 
                    await new Promise(resolve => setTimeout(resolve, 100));

                    handleAddPriceGroup(fields, getFormValues);
                  }}
                  disabled={isProcessing}
                  className={classes.addButton}
                  type="button"
                >
                  {isProcessing ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: 8 }} />
                      {t('Adding...')}
                    </>
                  ) : (
                    t('Add Price Group')
                  )}
                </Button>
              )}
            />
          </div>

          {priceGroups.length > 0 && (
            <TableContainer component={Paper} className={classes.table}>
              <Table aria-label="price groups table">
                <TableHead>
                  <TableRow>
                    <TableCell width="50px" />
                    <TableCell>{t('Rate Plan Title')}</TableCell>
                    <TableCell>{t('Date Range')}</TableCell>
                    <TableCell align="center">{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {priceGroups.map((group) => (
                    <PriceGroupRow
                      key={group?.id}
                      group={group}
                      onDelete={setGroupToDelete}
                      isDeleting={isDeleting}
                      isCurrentDeleting={isDeleting && group?.id === groupToDelete}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Dialog
            open={Boolean(groupToDelete)}
            onClose={() => setGroupToDelete(null)}
          >
            <DialogTitle>{t('Confirm Delete')}</DialogTitle>
            <DialogContent>
              {t('Are you sure you want to delete this rate plan?')}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setGroupToDelete(null)} color="primary">
                {t('Cancel')}
              </Button>
              <Button
                onClick={() => handleDeleteGroup(groupToDelete)}
                color="secondary"
                disabled={isDeleting}
              >
                {isDeleting ? <CircularProgress size={24} /> : t('Delete')}
              </Button>
            </DialogActions>
          </Dialog>

          <FormActionButtons
            cancelPath="/accommodations-and-activities"
            actionLabel={isUpdate ? t('Update') : t('Save')}
            setSubmitButton={setSubmitButton}
            isUpdate={isUpdate}
            disabled={isCreating || priceGroups.length === 0}
          />
        </form>
      </div>
    </>
  );
};

const selector = formValueSelector(FORM_NAME);

const mapStateToProps = state => ({
  formValues: state.form[FORM_NAME]?.values,
  dateRange: selector(state, 'dateRange'),
  businessOwner: state.businessOwner,
  pricesDailyScheduleForm: state.form.pricesDailyScheduleForm
});

const ConnectedForm = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  initialValues
})(ActivityRatePlan);

export default connect(mapStateToProps)(ConnectedForm);
